import { useQuery } from "@apollo/client";
import { Button, Modal } from "pepsico-ds";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { findRewardsByIds } from "../../../graphql/queries/findRewardsByIds";
import AddCatalogRewards from "../rulesAndOutcomes/outcomes/leaderboardOutcome/selectCatalogRewards/AddCatalogRewards";
import DistributionStrategySection from "./DistributionStrategySection";
import "./RewardAllocationConfig.scss";
import RewardItemConfig from "./RewardItemConfig";
import WinLimitsFields from "./WinLimitsFields";
import {
  DEFAULT_DISTRIBUTION_STRATEGIES,
  DEFAULT_INVENTORY_LIMIT_TYPES,
  DEFAULT_REWARD_FIELDS,
  DEFAULT_REWARD_STATUSES,
  DEFAULT_WIN_LIMIT_TYPES,
} from "./constants";

const extractFieldDefinitionsFromSchema = (outcomeDefinition) => {
  const inputSchema =
    outcomeDefinition?.inputSchema || outcomeDefinition?.input_schema;
  const configSchema = inputSchema?.properties?.reward_allocation_config?.properties
    ? inputSchema.properties.reward_allocation_config
    : inputSchema;
  const configProperties = configSchema?.properties;
  const configRequired = configSchema?.required || [];

  if (!configProperties) {
    return {
      distributionStrategies: DEFAULT_DISTRIBUTION_STRATEGIES,
      rewardStatuses: DEFAULT_REWARD_STATUSES,
      inventoryLimitTypes: DEFAULT_INVENTORY_LIMIT_TYPES,
      poolWinLimitTypes: DEFAULT_WIN_LIMIT_TYPES,
      rewardWinLimitTypes: DEFAULT_WIN_LIMIT_TYPES,
      rewardFields: DEFAULT_REWARD_FIELDS,
      configFields: {
        distribution_strategy: {
          title: "Distribution Strategy",
          description: "Strategy for distributing rewards to users",
          required: true,
        },
        rollover_enabled: {
          title: "Fallback Logic",
          description:
            "Assign the next eligible reward when the current reward is exhausted.",
          required: true,
        },
      },
    };
  }

  // Extract distribution strategies from enum
  const strategySchema = configProperties?.distribution_strategy || {};
  const distributionStrategies = (
    strategySchema.enum || ["ORDER_BASED", "PROBABILITY_BASED"]
  ).map((key) => ({
    key,
    label:
      strategySchema.enumLabels?.[key] ||
      key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  }));

  // Extract reward statuses from rewards[].status.enum
  const rewardSchema = configProperties?.rewards?.items?.properties || {};
  const statusSchema = rewardSchema?.status || {};
  const rewardStatuses = (statusSchema.enum || ["ACTIVE", "INACTIVE"]).map(
    (key) => ({
      key,
      label:
        statusSchema.enumLabels?.[key] || key.charAt(0) + key.slice(1).toLowerCase(),
    })
  );

  // Extract inventory limit types from rewards[].properties (fields with category: "inventory_limits")
  const inventoryLimitTypes = Object.entries(rewardSchema)
    .filter(([, schema]) => schema.category === "inventory_limits")
    .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
    .map(([key, schema]) => ({
      key,
      label:
        schema.title ||
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      description: schema.description || `Configure ${key.replace(/_/g, " ")}`,
    }));

  // Extract pool user win limit types from pool_user_win_limits.properties
  const poolWinSchema = configProperties?.pool_user_win_limits?.properties || {};
  const poolWinLimitTypes = Object.entries(poolWinSchema)
    .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
    .map(([key, schema]) => ({
      key,
      label:
        schema.title ||
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      description: schema.description || `Configure ${key.replace(/_/g, " ")}`,
    }));

  // Extract per-reward user win limit types from rewards[].user_win_limits.properties
  const rewardWinSchema = rewardSchema?.user_win_limits?.properties || {};
  const rewardWinLimitTypes = Object.entries(rewardWinSchema)
    .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
    .map(([key, schema]) => ({
      key,
      label:
        schema.title ||
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      description: schema.description || `Configure ${key.replace(/_/g, " ")}`,
    }));

  // Extract reward fields (non-limit fields from rewards[].properties)
  const rewardFields = Object.entries(rewardSchema)
    .filter(([key, schema]) => !schema.category && key !== "user_win_limits")
    .sort((a, b) => (a[1].display_order || 0) - (b[1].display_order || 0))
    .map(([key, schema]) => ({
      key,
      label:
        schema.title ||
        key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      type: schema.type,
      required: schema.required || false,
      readonly: schema.readonly || false,
      field_type:
        schema.field_type ||
        (schema.enum ? "dropdown" : schema.type === "boolean" ? "toggle" : "text"),
      required_when: schema.required_when,
      enum: schema.enum,
      enumLabels: schema.enumLabels,
      description: schema.description,
    }));

  // Extract config-level field definitions
  const configFields = {
    distribution_strategy: {
      title: strategySchema.title || "Distribution Strategy",
      description:
        strategySchema.description || "Strategy for distributing rewards to users",
      required:
        typeof strategySchema.required === "boolean"
          ? strategySchema.required
          : configRequired.length > 0
            ? configRequired.includes("distribution_strategy")
            : true,
    },
    rollover_enabled: {
      title: configProperties?.rollover_enabled?.title || "Fallback Logic",
      description:
        configProperties?.rollover_enabled?.description ||
        "Assign the next eligible reward when the current reward is exhausted.",
      required:
        typeof configProperties?.rollover_enabled?.required === "boolean"
          ? configProperties.rollover_enabled.required
          : configRequired.length > 0
            ? configRequired.includes("rollover_enabled")
            : true,
    },
  };

  return {
    distributionStrategies:
      distributionStrategies.length > 0
        ? distributionStrategies
        : DEFAULT_DISTRIBUTION_STRATEGIES,
    rewardStatuses:
      rewardStatuses.length > 0 ? rewardStatuses : DEFAULT_REWARD_STATUSES,
    inventoryLimitTypes:
      inventoryLimitTypes.length > 0
        ? inventoryLimitTypes
        : DEFAULT_INVENTORY_LIMIT_TYPES,
    poolWinLimitTypes:
      poolWinLimitTypes.length > 0 ? poolWinLimitTypes : DEFAULT_WIN_LIMIT_TYPES,
    rewardWinLimitTypes:
      rewardWinLimitTypes.length > 0 ? rewardWinLimitTypes : DEFAULT_WIN_LIMIT_TYPES,
    rewardFields: rewardFields.length > 0 ? rewardFields : DEFAULT_REWARD_FIELDS,
    configFields,
  };
};

/**
 * Build empty reward object dynamically from schema
 */
const buildEmptyReward = (
  rewardFields,
  inventoryLimitTypes,
  rewardWinLimitTypes
) => {
  const emptyReward = {};

  // Add reward fields with defaults
  rewardFields.forEach((field) => {
    if (field.type === "string") {
      emptyReward[field.key] = field.key === "status" ? "ACTIVE" : "";
    } else if (field.type === "integer" || field.type === "number") {
      emptyReward[field.key] = null;
    } else if (field.type === "boolean") {
      emptyReward[field.key] = false;
    }
  });

  // Add inventory limit fields
  inventoryLimitTypes.forEach((limit) => {
    emptyReward[limit.key] = null;
  });

  // Add user_win_limits object
  emptyReward.user_win_limits = {};
  rewardWinLimitTypes.forEach((limit) => {
    emptyReward.user_win_limits[limit.key] = null;
  });

  return emptyReward;
};

const hasText = (value) =>
  typeof value === "string"
    ? value.trim().length > 0
    : typeof value === "number"
      ? !Number.isNaN(value)
      : false;

const isMeaningfulReward = (reward) => {
  if (!reward || typeof reward !== "object") return false;
  const rewardId =
    typeof reward.reward_id === "string"
      ? reward.reward_id.trim()
      : typeof reward.reward_id === "number"
        ? `${reward.reward_id}`
        : "";
  const rewardName =
    typeof reward.reward_name === "string" ? reward.reward_name.trim() : "";

  // Defensive guard for legacy/generated placeholder rows.
  if (!rewardId && /^Reward\s*#\d+$/i.test(rewardName)) {
    return false;
  }

  // A valid reward should be catalog-backed; placeholder/default rows often carry
  // status/user_win_limits only, which should not render as an actual reward card.
  return hasText(rewardId) || hasText(rewardName);
};

const RewardAllocationConfig = ({
  config = {},
  outcomeDefinition,
  onChange,
  disabled = false,
  outcome,
  showAddReward = true,
  showDistributionStrategy = true,
  showMaxAllocationRule = true,
  showDeleteReward,
  setAutoPriority = false,
}) => {
  const [showRewardSelector, setShowRewardSelector] = useState(false);
  const [selectedCatalogs, setSelectedCatalogs] = useState({ rewards: [] });
  // Track if user has modified rewards (to prevent repopulating after deletion)
  const userModifiedRewards = useRef(false);

  // Fetch rewards from outcome.outcomeData.reward_ids
  const rewardIds = useMemo(
    () => outcome?.outcomeData?.reward_ids || [],
    [outcome?.outcomeData?.reward_ids]
  );
  const queryResult = useQuery(findRewardsByIds, {
    variables: { ids: rewardIds },
    skip: !rewardIds || rewardIds.length === 0,
  });
  const rewardsData = queryResult?.data;
  const rewardsLoading = queryResult?.loading;

  const rewardsMap = useMemo(() => {
    const map = {};
    if (rewardsData?.findRewardsByIds) {
      rewardsData.findRewardsByIds.forEach((reward) => {
        map[reward.id] = reward;
      });
    }
    return map;
  }, [rewardsData]);

  // Extract ALL field definitions from outcome definition schema
  const {
    distributionStrategies,
    rewardStatuses,
    inventoryLimitTypes,
    poolWinLimitTypes,
    rewardWinLimitTypes,
    rewardFields,
    configFields,
  } = useMemo(
    () => extractFieldDefinitionsFromSchema(outcomeDefinition),
    [outcomeDefinition]
  );

  const currentConfig = useMemo(() => {
    // Normalize distribution_strategy if it's an array (from Dropdown component)
    let strategy = config.distribution_strategy;
    if (Array.isArray(strategy) && strategy.length > 0) {
      strategy = strategy[0]?.id || strategy[0];
    } else if (typeof strategy === "object" && strategy?.id) {
      strategy = strategy.id;
    }

    // Build rewards array from config or from fetched reward data
    let rewards = (Array.isArray(config.rewards) ? config.rewards : []).filter(
      isMeaningfulReward
    );

    // If no rewards in config but we have fetched reward data, build from that
    // Only do this if user hasn't explicitly modified the rewards and data has loaded
    if (
      rewards.length === 0 &&
      !rewardsLoading &&
      Object.keys(rewardsMap).length > 0 &&
      !userModifiedRewards.current
    ) {
      rewards = rewardIds
        .map((rewardId) => {
          const rewardData = rewardsMap[rewardId];
          if (!rewardData) return null;

          return {
            reward_id: rewardData.id || rewardData.rewardId,
            reward_name: rewardData.name || rewardData.rewardName,
            imageUrl: rewardData.imageUrl || rewardData.image_url,
            status: "ACTIVE",
            user_win_limits: {},
          };
        })
        .filter(Boolean);
    }

    return {
      distribution_strategy: strategy || "ORDER_BASED",
      rollover_enabled: config.rollover_enabled ?? false,
      pool_user_win_limits: config.pool_user_win_limits || {},
      rewards,
    };
  }, [config, rewardsMap, rewardIds, rewardsLoading]);

  // Sync rewards from API data to outcomeData when loaded
  const hasPopulatedFromApi = useRef(false);
  useEffect(() => {
    // Only run once when rewards are populated from API and not yet synced
    if (
      !rewardsLoading &&
      Object.keys(rewardsMap).length > 0 &&
      !userModifiedRewards.current &&
      !hasPopulatedFromApi.current &&
      currentConfig.rewards.length > 0 &&
      (!config.rewards || config.rewards.filter(isMeaningfulReward).length === 0)
    ) {
      hasPopulatedFromApi.current = true;
      onChange(currentConfig);
    }
  }, [rewardsLoading, rewardsMap, currentConfig, config.rewards, onChange]);

  const handleConfigChange = useCallback(
    (field, value) => {
      const updated = { ...currentConfig, [field]: value };

      // When switching strategy, clear conditional fields on rewards
      if (field === "distribution_strategy") {
        updated.rewards = updated.rewards.map((r, idx) => ({
          ...r,
          priority_order:
            value === "ORDER_BASED"
              ? setAutoPriority
                ? idx + 1
                : r.priority_order
              : null,
          distribution_weight: value === "PROBABILITY_BASED" ? 0 : null,
        }));
      }

      // Auto-assign priority_order when setAutoPriority is enabled
      if (
        setAutoPriority &&
        field === "rewards" &&
        currentConfig.distribution_strategy === "ORDER_BASED"
      ) {
        updated.rewards = updated.rewards.map((r, idx) => ({
          ...r,
          priority_order: idx + 1,
        }));
      }

      onChange(updated);
    },
    [currentConfig, onChange, setAutoPriority]
  );

  const handlePoolLimitsChange = (newLimits) => {
    handleConfigChange("pool_user_win_limits", newLimits);
  };

  const handleRewardChange = (index, updatedReward) => {
    const newRewards = [...currentConfig.rewards];
    newRewards[index] = updatedReward;
    handleConfigChange("rewards", newRewards);
  };

  const handleAddReward = () => {
    setSelectedCatalogs({ rewards: [] });
    setShowRewardSelector(true);
  };

  const handleContinue = () => {
    if (!selectedCatalogs?.rewards?.length) {
      setShowRewardSelector(false);
      return;
    }

    // Build empty reward template dynamically from schema
    const emptyRewardTemplate = buildEmptyReward(
      rewardFields,
      inventoryLimitTypes,
      rewardWinLimitTypes
    );

    const newRewards = selectedCatalogs.rewards.map((reward, idx) => {
      const newReward = {
        ...emptyRewardTemplate,
        reward_id: reward.reward_id || reward.id,
        reward_name: reward.reward_name || reward.name,
        imageUrl: reward.imageUrl || reward.image_url,
        catalog_id: reward.catalog_id || reward.catalogId,
        user_win_limits: { ...emptyRewardTemplate.user_win_limits },
        status: "ACTIVE",
      };

      // Auto-set priority_order for ORDER_BASED
      if (currentConfig.distribution_strategy === "ORDER_BASED") {
        newReward.priority_order = currentConfig.rewards.length + idx + 1;
      }

      return newReward;
    });

    handleConfigChange("rewards", [...currentConfig.rewards, ...newRewards]);
    setShowRewardSelector(false);
    setSelectedCatalogs({ rewards: [] });
  };

  const handleRemoveReward = (index) => {
    userModifiedRewards.current = true; // Mark that user has modified rewards
    const newRewards = currentConfig.rewards.filter((_, i) => i !== index);
    handleConfigChange("rewards", newRewards);
  };

  const handleNumberWheelCapture = (e) => {
    const target = e.target;
    if (target instanceof HTMLInputElement && target.type === "number") {
      e.preventDefault();
      target.blur();
    }
  };

  return (
    <div
      className="reward-allocation-config"
      data-testid="reward-allocation-config"
      onWheelCapture={handleNumberWheelCapture}
    >
      {showDistributionStrategy && (
        <DistributionStrategySection
          distributionStrategy={currentConfig.distribution_strategy}
          rolloverEnabled={currentConfig.rollover_enabled}
          onDistributionStrategyChange={(value) =>
            handleConfigChange("distribution_strategy", value)
          }
          onRolloverChange={(checked) =>
            handleConfigChange("rollover_enabled", checked)
          }
          disabled={disabled}
          configFields={configFields}
          distributionStrategies={distributionStrategies}
        />
      )}

      <hr className="divider" />

      {/* User Win Frequency Limit (Campaign-Wide) */}
      <div className="mb-3">
        <div className="pool-limits-section">
          <WinLimitsFields
            title="User Win Frequency Limit"
            subtitle="Maximum number of wins a user can receive within a defined timeframe, regardless of reward type."
            limits={currentConfig.pool_user_win_limits}
            onChange={handlePoolLimitsChange}
            disabled={disabled}
            limitTypes={poolWinLimitTypes}
          />
          <p className="helper-text mt-2">
            Single counter per user per campaign outcome. Tracks total wins across
            ALL rewards in the pool.
          </p>
        </div>
      </div>

      <hr className="divider" />

      {/* Rewards Array */}
      <div className="rewards-section">
        <div className="display-flex justify-content-space-between align-items-center mb-2">
          <h4 className="font-md">Rewards ({currentConfig.rewards.length})</h4>
          {!disabled && showAddReward && (
            <Button
              text="Add Reward"
              iconLeading="add"
              variant="secondary"
              size="small"
              onClick={handleAddReward}
              data-testid="add-reward-button"
            />
          )}
        </div>

        {currentConfig.rewards.length === 0 ? (
          <div className="no-rewards-placeholder">
            <p>No rewards added yet. Click &quot;Add Reward&quot; to begin.</p>
          </div>
        ) : (
          currentConfig.rewards.map((reward, index) => (
            <RewardItemConfig
              key={`reward-${index}`}
              reward={reward}
              index={index}
              distributionStrategy={currentConfig.distribution_strategy}
              onChange={handleRewardChange}
              onRemove={showDeleteReward ? handleRemoveReward : undefined}
              disabled={disabled}
              showDeleteReward={showDeleteReward}
              inventoryLimitTypes={inventoryLimitTypes}
              winLimitTypes={rewardWinLimitTypes}
              rewardStatuses={rewardStatuses}
              rewardFields={rewardFields}
              showConfigurationSection={true}
              showMaximumAllocationsSection={showMaxAllocationRule}
              showMaximumAllocationsPerUserSection={true}
              setAutoPriority={setAutoPriority}
            />
          ))
        )}
      </div>

      <Modal
        showModal={showRewardSelector}
        onCloseModal={() => setShowRewardSelector(false)}
        title="Choose Rewards Catalog"
        primaryButtonProps={{
          text: "Next",
          onClick: handleContinue,
          disabled: !selectedCatalogs?.rewards?.length,
          size: "medium",
          variant: "primary",
        }}
        secondaryButtonProps={{
          text: "Cancel",
          onClick: () => setShowRewardSelector(false),
          size: "medium",
          variant: "tertiary",
        }}
        showTertiaryButton={false}
        showLink={false}
        className="reward-catalog-modal"
      >
        <AddCatalogRewards
          selectedCatalogs={selectedCatalogs}
          updateSelectedCatalogs={setSelectedCatalogs}
          isSingularMode={false}
          shouldSetDefaultValue={false}
        />
      </Modal>
    </div>
  );
};

RewardAllocationConfig.propTypes = {
  config: PropTypes.object,
  outcomeDefinition: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  allowDelete: PropTypes.bool,
  setAutoPriority: PropTypes.bool,
};

export default RewardAllocationConfig;
